const MAX_INGREDIENTS = 50;
const MAX_INGREDIENT_GROUPS = 12;
const MAX_STEPS = 40;
const MAX_TAGS = 8;
const MAX_TIPS = 8;
const DIFFICULTIES = new Set(['easy', 'medium', 'hard']);

const asTrimmedString = (value, maxLength = 160) => (
  typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
);

const asStringList = (value, maxItems, maxItemLength) => {
  if (!Array.isArray(value) || value.length === 0 || value.length > maxItems) return null;
  const items = value.map(item => asTrimmedString(item, maxItemLength));
  return items.some(item => !item) ? null : items;
};

const asOptionalList = (value, maxItems, maxItemLength) => (
  Array.isArray(value)
    ? value.map(item => asTrimmedString(item, maxItemLength)).filter(Boolean).slice(0, maxItems)
    : []
);

// Ingredients arrive either as plain strings or as { section, items } groups.
// Both may be mixed; strings attach to the trailing anonymous group so the
// model's ordering is preserved. The card renders from `groups`; `flat` keeps
// a single list for counts and accessibility summaries.
const parseIngredients = (value) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const groups = [];
  const flat = [];

  for (const entry of value) {
    if (typeof entry === 'string') {
      const item = asTrimmedString(entry, 160);
      if (!item) return null;
      let last = groups[groups.length - 1];
      if (!last || last.named) {
        last = { section: '', items: [], named: false };
        groups.push(last);
      }
      last.items.push(item);
      flat.push(item);
    } else if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const items = asStringList(entry.items, MAX_INGREDIENTS, 160);
      if (!items) return null;
      groups.push({
        section: asTrimmedString(entry.section, 80),
        items,
        named: true,
      });
      flat.push(...items);
    } else {
      return null;
    }

    if (groups.length > MAX_INGREDIENT_GROUPS || flat.length > MAX_INGREDIENTS) return null;
  }

  return {
    groups: groups.map(({ section, items }) => ({ section, items })),
    flat,
  };
};

export function parseRecipeSpec(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const title = asTrimmedString(parsed.title, 120);
    if (!title) return null;

    const ingredients = parseIngredients(parsed.ingredients);
    const steps = asStringList(parsed.steps, MAX_STEPS, 500);
    if (!ingredients || !steps) return null;

    const difficulty = asTrimmedString(parsed.difficulty, 20).toLowerCase();
    const servings = typeof parsed.servings === 'number' && Number.isFinite(parsed.servings)
      ? String(parsed.servings)
      : asTrimmedString(parsed.servings, 40);

    return {
      title,
      description: asTrimmedString(parsed.description, 400),
      prepTime: asTrimmedString(parsed.prepTime, 40),
      cookTime: asTrimmedString(parsed.cookTime, 40),
      totalTime: asTrimmedString(parsed.totalTime, 40),
      servings,
      difficulty: DIFFICULTIES.has(difficulty) ? difficulty : '',
      cuisine: asTrimmedString(parsed.cuisine, 60),
      tags: asOptionalList(parsed.tags, MAX_TAGS, 30),
      ingredients: ingredients.flat,
      ingredientGroups: ingredients.groups,
      steps,
      tips: asOptionalList(parsed.tips, MAX_TIPS, 300),
    };
  } catch (_) {
    return null;
  }
}
